/**
 * @fileoverview Client-only Arrow file uploads (POC).
 *
 * Lets a local `.arrow` file (Arrow IPC stream format — what the server writes
 * to `runs/{runId}.arrow`) be dropped straight into OPFS and surfaced as a
 * normal dataset, with no server row, no GCS object, and no `runs` record. Used
 * to probe how large an OPFS-backed payload the browser app can actually handle.
 *
 * Both the fake dataset rows and any blocks pointing at them are tagged with
 * `markSnapshotOrigin`, which is what stops the Legend State CRUD wrappers from
 * echoing them to the server (`blocks.datasetId` has a foreign key to
 * `datasets`, so a real block row could never reference a local-only dataset).
 * Because `syncedCrudPersist` runs in `mode: "set"`, every `list()` refresh
 * replaces the store contents — the rows are kept in this localStorage registry
 * and re-merged by the `list()` hooks in `datasets.crud.ts` / `blocks.crud.ts`.
 */

import { observable } from "@legendapp/state";
import { ObservablePersistLocalStorage } from "@legendapp/state/persist-plugins/local-storage";
import { synced } from "@legendapp/state/sync";
import { nanoid } from "@repo/shared/utils/id";
import { generateKeyBetween } from "fractional-indexing";
import { toast } from "sonner";

import { logger } from "~/libs/logger";
import duckdb from "~/workers/duckdb";
import {
  isRunCacheSupported,
  removeRunArrowFromOpfs,
  streamRunArrowToOpfs,
} from "~/workers/opfs-dataset-cache";
import { user$ } from "../auth/user.store";
import { blocks$ } from "../blocks/blocks.crud";
import type { Block } from "../blocks/blocks.types";
import { markSnapshotOrigin } from "../board-snapshot/snapshot-origins";
import { activeOrganization$ } from "../organizations/active-organization.store";
import { datasets$ } from "./datasets.crud";
import type { Dataset } from "./datasets.types";
import { ensureRunPayload } from "./rift-dataset-payload.query";

type LocalUploadRegistry = {
  /** Fake `Dataset` rows, keyed by dataset id. */
  datasets: Record<string, Dataset>;
  /** Client-only blocks that point at one of those datasets, keyed by block id. */
  blocks: Record<string, Block>;
};

const localUploads$ = observable(
  synced<LocalUploadRegistry>({
    initial: { datasets: {}, blocks: {} },
    persist: {
      name: "local-uploads",
      plugin: ObservablePersistLocalStorage,
    },
  }),
);

/**
 * localStorage round-trips `Date` fields as ISO strings. Consumers treat these
 * as real dates (`new Date(...)`, date formatters), so revive them on the way
 * out of the registry rather than leaking strings into `datasets$`.
 */
const reviveDataset = (dataset: Dataset): Dataset => ({
  ...dataset,
  createdAt: new Date(dataset.createdAt),
  updatedAt: new Date(dataset.updatedAt),
  fetchedAt: dataset.fetchedAt ? new Date(dataset.fetchedAt) : null,
});

const reviveBlock = (block: Block): Block => ({
  ...block,
  createdAt: new Date(block.createdAt),
  updatedAt: new Date(block.updatedAt),
});

/** True when `id` is a locally uploaded dataset rather than a server row. */
export const isLocalUploadDataset = (
  id: string | null | undefined,
): boolean => {
  if (!id) return false;
  return localUploads$.datasets[id].peek() != null;
};

/** True when `id` is a client-only block backed by a local upload. */
export const isLocalUploadBlock = (id: string | null | undefined): boolean => {
  if (!id) return false;
  return localUploads$.blocks[id].peek() != null;
};

/** Registry rows re-merged into `datasets$` on every CRUD `list()`. */
export const getLocalUploadDatasets = (): Dataset[] =>
  Object.values(localUploads$.datasets.peek() ?? {}).map(reviveDataset);

/** Registry rows re-merged into `blocks$` on every CRUD `list()`. */
export const getLocalUploadBlocks = (): Block[] =>
  Object.values(localUploads$.blocks.peek() ?? {}).map(reviveBlock);

/**
 * Mirrors a partial dataset edit (rift config, column config, rename) into the
 * registry so it survives the next `list()` refresh. Server sync is skipped for
 * these rows, so without this the edit would be reverted.
 */
export const mergeLocalUploadDataset = (
  id: string,
  patch: Partial<Dataset>,
): void => {
  const existing = localUploads$.datasets[id].peek();
  if (!existing) return;
  localUploads$.datasets[id].set({ ...existing, ...patch, id });
};

/** Registers a client-only block and mirrors it into the registry. */
export const saveLocalUploadBlock = (block: Block): void => {
  markSnapshotOrigin(block.id);
  localUploads$.blocks[block.id].set(block);
};

/** Mirrors a partial block edit (rift config, layout, chart config) into the registry. */
export const mergeLocalUploadBlock = (
  id: string,
  patch: Partial<Block>,
): void => {
  const existing = localUploads$.blocks[id].peek();
  if (!existing) return;
  localUploads$.blocks[id].set({ ...existing, ...patch, id });
};

export const removeLocalUploadBlock = (id: string): void => {
  localUploads$.blocks[id].delete();
};

/**
 * Drops a local dataset from the registry along with its OPFS payload and any
 * client-only blocks that referenced it. Server rows cascade on the
 * `blocks.datasetId` foreign key; these have to be swept by hand.
 */
export const removeLocalUploadDataset = (id: string): void => {
  const dataset = localUploads$.datasets[id].peek();
  localUploads$.datasets[id].delete();

  for (const block of Object.values(localUploads$.blocks.peek() ?? {})) {
    if (block.datasetId !== id) continue;
    localUploads$.blocks[block.id].delete();
    blocks$[block.id].delete();
  }

  if (dataset?.runId) {
    removeRunArrowFromOpfs(dataset.runId).catch((err: unknown) => {
      logger.error(
        { err, runId: dataset.runId },
        "local upload cleanup failed",
      );
    });
  }
};

/**
 * Fractional-indexing order key placing the upload after the last shared
 * dataset in the sidebar list.
 */
const nextSharedDatasetOrder = (): string => {
  const shared = Object.values(datasets$.get() ?? {}).filter((d) => !d.private);
  shared.sort((a, b) => (a.order < b.order ? -1 : a.order > b.order ? 1 : 0));
  return generateKeyBetween(shared.at(-1)?.order ?? null, null);
};

/** Reads the row count of a registered run without materialising any rows. */
const readRunRowCount = async (runId: string): Promise<number | null> => {
  const { row } = await duckdb.queryRow(
    `SELECT COUNT(*) AS row_count FROM '${runId}.arrow'`,
  );
  const value = row?.row_count;
  if (value == null) return null;
  const count = Number(value);
  return Number.isFinite(count) ? count : null;
};

/**
 * Ingests a local Arrow IPC file as a client-only dataset: streams it into OPFS
 * under a synthetic run id, registers it with DuckDB, derives the column schema
 * from the registered file, and injects a fake `Dataset` row. Returns the new
 * dataset id.
 *
 * The bytes never enter the JS heap in one piece — `file.stream()` is piped
 * straight to OPFS and DuckDB reads the resulting file by handle.
 */
export const uploadLocalArrowDataset = async (file: File): Promise<string> => {
  if (!isRunCacheSupported()) {
    throw new Error(
      "This browser has no OPFS support, so uploads can't be cached",
    );
  }

  const runId = crypto.randomUUID();
  await streamRunArrowToOpfs(runId, file.stream(), {
    expectedBytes: file.size,
  });

  try {
    await ensureRunPayload({ runId });
    const { columns } = await duckdb.queryTable(
      `SELECT * FROM '${runId}.arrow' LIMIT 0`,
    );
    if (Object.keys(columns).length === 0) {
      throw new Error("No columns found — is this an Arrow IPC stream file?");
    }
    const rowCount = await readRunRowCount(runId);

    const datasetId = nanoid();
    const now = new Date();
    const dataset: Dataset = {
      id: datasetId,
      name: file.name.replace(/\.arrow$/i, "") || "Local upload",
      organizationId: activeOrganization$.id.get() as string,
      datasourceId: null,
      ownerId: user$.id.get(),
      query: "",
      draftQuery: "",
      private: false,
      favorite: false,
      verified: false,
      verifiedBy: null,
      verifiedAt: null,
      avatar: null,
      columnsConfig: {},
      riftConfig: null,
      columns,
      runId,
      fetchedAt: now,
      rowCount,
      sizeBytes: file.size,
      folderId: null,
      order: nextSharedDatasetOrder(),
      status: "IDLE",
      createdAt: now,
      updatedAt: now,
    };

    markSnapshotOrigin(datasetId);
    localUploads$.datasets[datasetId].set(dataset);
    datasets$[datasetId].set(dataset);

    return datasetId;
  } catch (err) {
    await removeRunArrowFromOpfs(runId).catch(() => undefined);
    throw err;
  }
};

/** Resolves with the chosen file, or `null` if the picker was dismissed. */
const pickArrowFile = (): Promise<File | null> =>
  new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".arrow";
    // Chrome only fires `cancel` on an input that is in the document.
    input.style.display = "none";
    document.body.append(input);

    const finish = (file: File | null) => {
      input.remove();
      resolve(file);
    };

    input.addEventListener("change", () => finish(input.files?.[0] ?? null), {
      once: true,
    });
    input.addEventListener("cancel", () => finish(null), { once: true });
    input.click();
  });

/**
 * Opens a native file picker for an Arrow file and ingests the selection.
 * Resolves with the new dataset id, or `null` when the user cancels or the
 * upload fails (failures are surfaced as a toast).
 */
export const pickAndUploadLocalArrowDataset = async (): Promise<
  string | null
> => {
  const file = await pickArrowFile();
  if (!file) return null;

  const toastId = toast.loading(`Loading ${file.name}...`);
  try {
    const datasetId = await uploadLocalArrowDataset(file);
    const rowCount = datasets$[datasetId].rowCount.peek();
    toast.success(
      rowCount == null
        ? `Loaded ${file.name}`
        : `Loaded ${file.name} (${rowCount.toLocaleString()} rows)`,
      { id: toastId },
    );
    return datasetId;
  } catch (err) {
    logger.error({ err, fileName: file.name }, "local arrow upload failed");
    toast.error(err instanceof Error ? err.message : "Upload failed", {
      id: toastId,
    });
    return null;
  }
};

// Origin tags live in memory only, so re-apply them for the persisted registry
// before any CRUD wrapper can try to sync one of these rows to the server.
if (typeof window !== "undefined") {
  for (const id of Object.keys(localUploads$.datasets.peek() ?? {})) {
    markSnapshotOrigin(id);
  }
  for (const id of Object.keys(localUploads$.blocks.peek() ?? {})) {
    markSnapshotOrigin(id);
  }
}
